import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-wallets';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';
import { ChakraProvider } from '@chakra-ui/react';
import { Toaster } from 'react-hot-toast';

// Import theme
import theme from './theme';
import { SOLANA_RPC_URL } from './config';

// Import pages
import Portfolio from './pages/Portfolio';
import EnhancedExplorer from './pages/EnhancedExplorer.js';
import DesolAI from './pages/DesolAI';
import Governance from './pages/Governance';
import Wallet from './pages/Wallet';
import TokenPage from './pages/TokenPage';
// Rewards functionality integrated into Wallet page
import NotFound from './pages/NotFound';

// Import components
import Header from './components/Header';
import Sidebar from './components/Sidebar';

// Import styles
import './App.css';
import '@solana/wallet-adapter-react-ui/styles.css';

function App() {
  // Set up Solana network and wallet
  const network = WalletAdapterNetwork.Mainnet;
  // Use the RPC URL from config or fallback to the clusterApiUrl
  const endpoint = useMemo(() => SOLANA_RPC_URL || clusterApiUrl(network), [network]);
  const wallets = useMemo(() => [new PhantomWalletAdapter()], []);

  return (
    <ChakraProvider theme={theme}>
      <ConnectionProvider endpoint={endpoint}>
        <WalletProvider wallets={wallets} autoConnect>
          <WalletModalProvider>
            <div className="app">
              <Header />
              <div className="main-container">
                <Sidebar />
                <main className="content">
                  <Routes>
                    <Route path="/" element={<Portfolio />} />
                    <Route path="/explorer" element={<EnhancedExplorer />} />
                    <Route path="/ai" element={<DesolAI />} />
                    <Route path="/governance" element={<Governance />} />
                    <Route path="/wallet" element={<Wallet />} />
                    <Route path="/token" element={<TokenPage />} />
                    <Route path="/rewards" element={<Navigate to="/wallet" replace />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </div>
            </div>
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: '#24243e',
                  color: '#ffffff',
                  borderRadius: '0.75rem',
                  border: '1px solid #30304e',
                },
                success: {
                  iconTheme: {
                    primary: '#00FF9D',
                    secondary: 'rgba(0, 255, 157, 0.2)',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#FF0066',
                    secondary: 'rgba(255, 0, 102, 0.2)',
                  },
                },
              }}
            />
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </ChakraProvider>
  );
}

export default App;